import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { MODULE_THEMES } from "../data/modules";
import { Trophy, Flame, Target, Award, CheckCircle2, Lock } from "lucide-react";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm border border-black/5">
      <div
        className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
        style={{ backgroundColor: `${color}1A` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <p className="font-head font-extrabold text-3xl">{value}</p>
      <p className="text-sm text-muted-foreground font-semibold mt-1">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data)
    return (
      <div className="min-h-screen">
        <Navbar />
        <p className="text-center text-muted-foreground mt-20">Loading your progress…</p>
      </div>
    );

  const earnedIds = new Set(data.badges.map((b) => b.id));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 py-10">
        <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight animate-fade-up">
          Your progress
        </h1>
        <p className="text-muted-foreground mt-2 animate-fade-up">
          Every answer is a step forward. Here's how far you've come.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8 animate-fade-up">
          <Stat icon={Trophy} label="Total points" value={data.points} color="#D4AF37" />
          <Stat icon={Flame} label="Day streak" value={data.streak} color="#FF7B54" />
          <Stat icon={CheckCircle2} label="Correct answers" value={data.total_correct} color="#48BB78" />
          <Stat icon={Target} label="Questions tried" value={data.total_answered} color="#7B9EBF" />
        </div>

        {/* Per module progress */}
        <h2 className="font-head font-extrabold text-xl mt-12 mb-4">Program mastery</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.per_module.map((m) => {
            const t = MODULE_THEMES[m.key];
            const pct = m.total ? Math.round((m.correct / m.total) * 100) : 0;
            return (
              <div
                key={m.key}
                className="bg-white rounded-3xl p-6 shadow-sm border border-black/5"
                data-testid={`dash-module-${m.key}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-head font-bold">{m.name}</p>
                  <span className="text-sm font-bold" style={{ color: t.primary }}>
                    {pct}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-black/5 overflow-hidden mt-3">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: t.primary }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2 font-semibold">
                  {m.correct}/{m.total} mastered · {m.answered} attempted
                </p>
              </div>
            );
          })}
        </div>

        {/* Badges */}
        <h2 className="font-head font-extrabold text-xl mt-12 mb-4">Badges</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {data.all_badges.map((b) => {
            const earned = earnedIds.has(b.id);
            return (
              <div
                key={b.id}
                className={`rounded-3xl p-5 text-center border ${
                  earned ? "bg-[#FFFBEB] border-[#F6E05E]/40" : "bg-white border-black/5 opacity-60"
                }`}
                data-testid={`badge-${b.id}`}
              >
                <div
                  className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center"
                  style={{ backgroundColor: earned ? "#D4AF37" : "#EDF2F7" }}
                >
                  {earned ? (
                    <Award className="w-6 h-6 text-white" />
                  ) : (
                    <Lock className="w-5 h-5 text-[#A0AEC0]" />
                  )}
                </div>
                <p className="font-bold text-sm mt-3 leading-tight">{b.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
